import {
  ORDER_STATUS_CANCELLED,
  TestTypes
} from "constants/v3/generalConstants";
import {
  GlDecoratedSurveyAnswer,
  FlattenedSurveyAnswers,
  flattenSurveyAnswers,
  hasAnsweredAllCriticalKitRequestQuestions
} from "utils/v3/surveyUtils";
import { Kit } from "types/kit";
type KitOverrideConfig = {
  [key: string]: TestTypes[];
};

// Kit override configurations
const KIT_OVERRIDE_CONFIGS: KitOverrideConfig = {
  "?dzqDB37ztZUE": ["GHS"],
  "?RVvrWqsPvK3U": ["GHS", "PGX"],
  "?Uvg9wDZtEkcZ": ["GHS", "CARRIER"],
  "?BhaLg9NLZsfk": ["GHS", "CARRIER", "PGX"],
  "?mALQkApUBY5s": ["PGX"],
  "?x8pqSgd7CHa2": ["CARRIER", "PGX"],
  "?DAr8jmG7yv88": ["CARRIER"]
};

export const kitsGLOffersThatThisMemberHasntOrderedOrThatWereCancelled = (
  kitsGLCurrentlyOffers: Kit[],
  kitsAlreadyOrdered: Kit[]
): Kit[] => {
  const orderedTestTypes = new Set(
    kitsAlreadyOrdered
      .filter((order) => order.glOrderStatus !== ORDER_STATUS_CANCELLED)
      .map((order) => order.glTestType)
  );

  return kitsGLCurrentlyOffers.filter(
    (kit) => !orderedTestTypes.has(kit.glTestType)
  );
};

export const doesMemberNeedToTalkToNavBasedOnFlattenedSurveyAnswers = (
  surveyAnswers: FlattenedSurveyAnswers
): boolean => {
  return (
    surveyAnswers["active-cancer-diagnosis"] === true ||
    (surveyAnswers["dna-transplant"] &&
      surveyAnswers["dna-transplant"] !== "Neither") ||
    (surveyAnswers["have-you-had-a-dna-transplant"] &&
      surveyAnswers["have-you-had-a-dna-transplant"] !== "no") ||
    (surveyAnswers["have-you-ever-been-told-you-have-cancer"] === true &&
      surveyAnswers["cancer-type"] !==
        "basal-cell-or-squamous-cell-skin-cancer")
  );
};

const filterKitsByEligibility = (
  kits: Kit[],
  surveyAnswers: FlattenedSurveyAnswers,
  glProgramId: string
): Kit[] => {
  return kits.filter((kit) => {
    // GHS is always available
    if (kit.glTestType === "GHS") {
      return true;
    }

    // GHS only program, no tests below are ever available
    if (glProgramId === "Genetic Screen") {
      return false;
    }

    // Carrier Screen eligibility
    if (kit.glTestType === "CARRIER") {
      return ["yes", "not-sure"].includes(
        surveyAnswers["you-or-partner-pregnant-or-planning-in-24-months"]
      );
    }

    // PGx test eligibility
    if (kit.glTestType === "PGX") {
      // If no survey answers, don't suggest PGX
      if (!surveyAnswers) return false;

      return (
        surveyAnswers["taking-5-plus-medications"] === true ||
        ["yes", "not-sure"].includes(
          surveyAnswers["taking-following-types-medication-with-not-sure"]
        )
      );
    }

    return false;
  });
};

export const figureOutKitsToOfferMemberRightNow = (
  kitsGLCurrentlyOffers: Kit[],
  kitsAlreadyOrdered: Kit[],
  surveyAnswers: GlDecoratedSurveyAnswer[],
  kitSuggestionOverrideToken?: string,
  glProgramId?: string
): Kit[] => {
  // Check if critical kit request questions have been answered
  if (!hasAnsweredAllCriticalKitRequestQuestions(surveyAnswers)) {
    return [];
  }

  // Handle override tokens if present
  if (
    kitSuggestionOverrideToken &&
    KIT_OVERRIDE_CONFIGS[kitSuggestionOverrideToken]
  ) {
    return kitsGLCurrentlyOffers.filter((kit) =>
      KIT_OVERRIDE_CONFIGS[kitSuggestionOverrideToken].includes(kit.glTestType)
    );
  }

  // Get available kits (not already ordered)
  const availableKits = kitsGLOffersThatThisMemberHasntOrderedOrThatWereCancelled(
    kitsGLCurrentlyOffers,
    kitsAlreadyOrdered
  );

  // Convert survey answers array to object for easier access
  const flattenedSurveyAnswers = flattenSurveyAnswers(surveyAnswers);

  // Check if member needs to talk to Nav before ordering tests
  if (
    doesMemberNeedToTalkToNavBasedOnFlattenedSurveyAnswers(
      flattenedSurveyAnswers
    )
  ) {
    return [];
  }

  // Filter kits based on eligibility criteria
  return filterKitsByEligibility(
    availableKits,
    flattenedSurveyAnswers,
    glProgramId || ""
  );
};

export const testKitUtils = {
  figureOutKitsToOfferMemberRightNow
};

/**
 * Gets the display name for a test kit order
 * @param kit - The kit to get the display name for
 * @param kits - Array of all available kits to search through
 * @returns The display name of the kit, either from the matching test type or from the kit's own description
 */
export function getTestKitDisplayName({
  kit,
  kits
}: {
  kit: Kit;
  kits: Kit[];
}) {
  const currentTestKit = kit?.glTestType
    ? kits.find((currentKit) => currentKit.glTestType === kit.glTestType)
    : kit;

  return (
    currentTestKit?.test_name ?? currentTestKit?.name ?? kit?.description?.name
  );
}

export const pushDescriptionIntoKitObject = (kitArray: Kit[]) => {
  const kitArrayDescription: Record<
    string,
    { chipInformation?: string; description: string }
  > = {
    ghs_test_uuid: {
      description:
        "Understand your health with this accurate, medical-grade DNA test that identifies a predisposition to developing hereditary cancers, cardiovascular diseases, and other significant conditions."
    },
    "3cf65de6-41f9-4e98-a256-231c871e3377": {
      // Legacy test kit
      description:
        "Understand your health with this accurate, medical-grade DNA test that identifies a predisposition to developing hereditary cancers, cardiovascular diseases, and other significant conditions."
    },
    "dna-visit-ghs": {
      description:
        "Understand your health with this accurate, medical-grade DNA test that identifies a predisposition to developing hereditary cancers, cardiovascular diseases, and other significant conditions."
    },
    "pwn-health-invitae-ghs": {
      description:
        "Understand your health with this accurate, medical-grade DNA test that identifies a predisposition to developing hereditary cancers, cardiovascular diseases, and other significant conditions."
    },
    carrier_test_uuid: {
      description:
        "Receive answers and inform family planning with carrier screening that examines your genetics to identify a potential risk of having a child affected by a genetic condition.",
      chipInformation: "Planning on having a child?"
    },
    "71957dc2-ee1e-4d9d-bc72-507aeb0ff992": {
      // Legacy test kit
      description:
        "Learn what drugs work for you with a PGx test that uncovers how you metabolize and respond to medications. This knowledge helps reduce adverse drug reactions.",
      chipInformation: "Taking medications?"
    },
    "8845bdbe-330e-4afc-82ed-f1cfa1d808ad": {
      // Legacy test kit
      description:
        "Learn what drugs work for you with a PGx test that uncovers how you metabolize and respond to medications. This knowledge helps reduce adverse drug reactions.",
      chipInformation: "Taking medications?"
    },
    pgx_test_uuid: {
      // OneOme test kit - current
      description:
        "Learn what drugs work for you with a PGx test that uncovers how you metabolize and respond to medications. This knowledge helps reduce adverse drug reactions.",
      chipInformation: "Taking medications?"
    },
    "429fc77b-c1c1-455e-937c-de67db27e058": {
      // Stage OneOme kit
      description:
        "Learn what drugs work for you with a PGx test that uncovers how you metabolize and respond to medications. This knowledge helps reduce adverse drug reactions.",
      chipInformation: "Taking medications?"
    },
    "dna-visit-accessdx-pgx": {
      // AccessDx test kit
      description:
        "Learn what drugs work for you with a PGx test that uncovers how you metabolize and respond to medications. This knowledge helps reduce adverse drug reactions.",
      chipInformation: "Taking medications?"
    }
  };

  return kitArray.map((kit: Kit) => {
    return {
      ...kit,
      ...kitArrayDescription[
        kit.test_uuid ?? `${kit.glTestType?.toLowerCase()}_ghs_test_uuid`
      ]
    };
  });
};
